<script lang="ts">
  // App-styled calendar date picker. Replaces native <input type="date"> so the
  // popup matches the app theme (and behaves consistently inside the Telegram
  // WebView). Value is an ISO 'YYYY-MM-DD' string; '' means unset.
  interface Props {
    value?: string;
    label?: string;
    placeholder?: string;
    min?: string;
    max?: string;
    /** When true the value carries a time too ('YYYY-MM-DDTHH:mm') and the popup
     *  shows hour/minute selectors. Picking a day no longer auto-closes. */
    withTime?: boolean;
    onChange?: (v: string) => void;
    /** Range mode: pick start then end on one calendar. Uses from/to/onRange. */
    mode?: 'single' | 'range';
    from?: string;
    to?: string;
    onRange?: (from: string, to: string) => void;
    /** Range mode: fired when the user confirms via the footer button. */
    onApply?: () => void;
    /** Range mode: label of the confirm button (default «Застосувати»). */
    applyLabel?: string;
    /** Range mode: also pick a start/end time (from/to become 'YYYY-MM-DDTHH:mm',
     *  defaults 00:00 / 23:59). */
    rangeTime?: boolean;
  }
  let {
    value = '',
    label,
    placeholder = 'Оберіть дату',
    min,
    max,
    withTime = false,
    onChange = () => {},
    mode = 'single',
    from = '',
    to = '',
    onRange = () => {},
    onApply = () => {},
    applyLabel = 'Застосувати',
    rangeTime = false,
  }: Props = $props();

  // Range-mode time-of-day (only when rangeTime). from/to carry 'YYYY-MM-DDTHH:mm'.
  let startTime = $state('00:00');
  let endTime = $state('23:59');
  const timeOf = (s: string, def: string): string => (s.includes('T') ? s.split('T')[1] : def);

  const MONTHS = [
    'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
    'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень',
  ];
  const WD = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'];
  const HOURS = Array.from({ length: 24 }, (_, i) => i);
  const MINUTES = Array.from({ length: 60 }, (_, i) => i);

  const pad = (n: number): string => String(n).padStart(2, '0');
  const iso = (y: number, m: number, d: number): string => `${y}-${pad(m + 1)}-${pad(d)}`;
  /** Date portion 'YYYY-MM-DD' (drops any 'THH:mm'). */
  const datePart = (s: string): string => (s ? s.split('T')[0] : '');
  function timePart(s: string): { h: number; mm: number } {
    const t = s.includes('T') ? s.split('T')[1] ?? '' : '';
    const [h, m] = t.split(':').map(Number);
    return { h: Number.isFinite(h) ? h : 0, mm: Number.isFinite(m) ? m : 0 };
  }
  function parse(s: string): { y: number; m: number; d: number } {
    const [y, m, d] = datePart(s).split('-').map(Number);
    return { y, m: m - 1, d };
  }
  function todayIso(): string {
    const n = new Date();
    return iso(n.getFullYear(), n.getMonth(), n.getDate());
  }

  let open = $state(false);
  let view = $state<{ y: number; m: number }>(initView());

  const curDate = $derived(datePart(value));
  const curTime = $derived(timePart(value));

  function initView(): { y: number; m: number } {
    const base = mode === 'range' ? from : value;
    if (base) {
      const p = parse(base);
      return { y: p.y, m: p.m };
    }
    const n = new Date();
    return { y: n.getFullYear(), m: n.getMonth() };
  }

  const fmtDay = (s: string): string => {
    const p = parse(s);
    return `${pad(p.d)}.${pad(p.m + 1)}.${p.y}`;
  };
  const dtLabel = (s: string, def: string): string =>
    `${fmtDay(s)}${rangeTime ? ` ${timeOf(s, def)}` : ''}`;
  const rangeDisplay = $derived(
    from && to
      ? `${dtLabel(from, '00:00')} – ${dtLabel(to, '23:59')}`
      : from
        ? `${dtLabel(from, '00:00')} – …`
        : '',
  );

  function openPicker(): void {
    view = initView();
    if (mode === 'range' && rangeTime) {
      startTime = timeOf(from, '00:00');
      endTime = timeOf(to, '23:59');
    }
    open = true;
  }

  const displayValue = $derived(
    value
      ? (() => {
          const p = parse(value);
          const base = `${pad(p.d)}.${pad(p.m + 1)}.${p.y}`;
          if (!withTime) return base;
          const t = timePart(value);
          return `${base}, ${pad(t.h)}:${pad(t.mm)}`;
        })()
      : '',
  );

  const grid = $derived.by(() => {
    const first = new Date(view.y, view.m, 1);
    const startOffset = (first.getDay() + 6) % 7; // Monday-first
    const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
    const n = new Date();
    const today = iso(n.getFullYear(), n.getMonth(), n.getDate());
    const cells: Array<{
      day: number; iso: string; disabled: boolean; selected: boolean; today: boolean;
      rStart: boolean; rEnd: boolean; inRange: boolean;
    } | null> = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const s = iso(view.y, view.m, d);
      const disabled = (!!min && s < min) || (!!max && s > max);
      const isRange = mode === 'range';
      const fromD = datePart(from);
      const toD = datePart(to);
      cells.push({
        day: d,
        iso: s,
        disabled,
        selected: !isRange && s === curDate,
        today: s === today,
        rStart: isRange && !!fromD && s === fromD,
        rEnd: isRange && !!toD && s === toD,
        inRange: isRange && !!fromD && !!toD && s > fromD && s < toD,
      });
    }
    return cells;
  });

  function prevMonth(): void {
    view = view.m === 0 ? { y: view.y - 1, m: 11 } : { y: view.y, m: view.m - 1 };
  }
  function nextMonth(): void {
    view = view.m === 11 ? { y: view.y + 1, m: 0 } : { y: view.y, m: view.m + 1 };
  }
  function pick(s: string): void {
    if (mode === 'range') {
      // First click (or after a complete range) starts a new selection; the
      // second sets the end (swapping if before the start). Stays open until
      // the user confirms via the footer button.
      const withT = (day: string, t: string) => (rangeTime ? `${day}T${t}` : day);
      const fromD = datePart(from);
      const toD = datePart(to);
      if (!fromD || (fromD && toD)) {
        onRange(withT(s, startTime), '');
      } else {
        const a = s < fromD ? s : fromD;
        const b = s < fromD ? fromD : s;
        onRange(withT(a, startTime), withT(b, endTime));
      }
      return;
    }
    if (withTime) {
      const t = timePart(value);
      onChange(`${s}T${pad(t.h)}:${pad(t.mm)}`);
      // keep open — the user still sets the time
    } else {
      onChange(s);
      open = false;
    }
  }
  function setHour(h: number): void {
    onChange(`${curDate || todayIso()}T${pad(h)}:${pad(curTime.mm)}`);
  }
  function setMin(m: number): void {
    onChange(`${curDate || todayIso()}T${pad(curTime.h)}:${pad(m)}`);
  }
  function setStartTime(t: string): void {
    startTime = t;
    const fd = datePart(from);
    if (fd) onRange(`${fd}T${t}`, to);
  }
  function setEndTime(t: string): void {
    endTime = t;
    const td = datePart(to);
    if (td) onRange(from, `${td}T${t}`);
  }
  // Hour/minute parts for the app-styled time selects.
  const sH = $derived(Number(startTime.split(':')[0]) || 0);
  const sM = $derived(Number(startTime.split(':')[1]) || 0);
  const eH = $derived(Number(endTime.split(':')[0]) || 0);
  const eM = $derived(Number(endTime.split(':')[1]) || 0);
  const setStartHM = (h: number, m: number): void => setStartTime(`${pad(h)}:${pad(m)}`);
  const setEndHM = (h: number, m: number): void => setEndTime(`${pad(h)}:${pad(m)}`);
  function clear(): void {
    if (mode === 'range') {
      startTime = '00:00';
      endTime = '23:59';
      onRange('', '');
    } else {
      onChange('');
    }
    open = false;
  }
</script>

<div class="dp">
  {#if label}<span class="dp-label">{label}</span>{/if}
  <button type="button" class="dp-field" class:empty={mode === 'range' ? !from : !value} onclick={openPicker}>
    <span>{mode === 'range' ? rangeDisplay || placeholder : displayValue || placeholder}</span>
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  </button>
</div>

{#if open}
  <button type="button" class="dp-backdrop" aria-label="Закрити" onclick={() => (open = false)}></button>
  <div class="dp-pop" role="dialog" aria-modal="true" aria-label={label ?? 'Календар'}>
    <div class="dp-head">
      <button type="button" class="dp-nav" onclick={prevMonth} aria-label="Попередній місяць">‹</button>
      <div class="dp-title">{MONTHS[view.m]} {view.y}</div>
      <button type="button" class="dp-nav" onclick={nextMonth} aria-label="Наступний місяць">›</button>
    </div>
    <div class="dp-wd">
      {#each WD as w (w)}<div class="dp-wd-cell">{w}</div>{/each}
    </div>
    <div class="dp-grid">
      {#each grid as c, i (i)}
        {#if c}
          <button
            type="button"
            class="dp-day"
            class:selected={c.selected}
            class:today={c.today}
            class:r-start={c.rStart}
            class:r-end={c.rEnd}
            class:in-range={c.inRange}
            disabled={c.disabled}
            onclick={() => pick(c.iso)}
          >{c.day}</button>
        {:else}
          <span class="dp-day-empty"></span>
        {/if}
      {/each}
    </div>
    {#if withTime}
      <div class="dp-time">
        <span class="dp-time-label">Час</span>
        <div class="dp-time-fields">
          <select
            class="dp-time-sel"
            value={curTime.h}
            onchange={(e) => setHour(Number((e.target as HTMLSelectElement).value))}
          >
            {#each HOURS as h (h)}<option value={h}>{pad(h)}</option>{/each}
          </select>
          <span class="dp-time-colon">:</span>
          <select
            class="dp-time-sel"
            value={curTime.mm}
            onchange={(e) => setMin(Number((e.target as HTMLSelectElement).value))}
          >
            {#each MINUTES as m (m)}<option value={m}>{pad(m)}</option>{/each}
          </select>
        </div>
      </div>
    {/if}
    {#if mode === 'range' && rangeTime}
      <div class="dp-range-times">
        <div class="dp-rt">
          <span>Час початку</span>
          <div class="dp-rt-fields">
            <select class="dp-time-sel" value={sH} onchange={(e) => setStartHM(Number((e.target as HTMLSelectElement).value), sM)}>
              {#each HOURS as h (h)}<option value={h}>{pad(h)}</option>{/each}
            </select>
            <span class="dp-time-colon">:</span>
            <select class="dp-time-sel" value={sM} onchange={(e) => setStartHM(sH, Number((e.target as HTMLSelectElement).value))}>
              {#each MINUTES as m (m)}<option value={m}>{pad(m)}</option>{/each}
            </select>
          </div>
        </div>
        <div class="dp-rt">
          <span>Час кінця</span>
          <div class="dp-rt-fields">
            <select class="dp-time-sel" value={eH} onchange={(e) => setEndHM(Number((e.target as HTMLSelectElement).value), eM)}>
              {#each HOURS as h (h)}<option value={h}>{pad(h)}</option>{/each}
            </select>
            <span class="dp-time-colon">:</span>
            <select class="dp-time-sel" value={eM} onchange={(e) => setEndHM(eH, Number((e.target as HTMLSelectElement).value))}>
              {#each MINUTES as m (m)}<option value={m}>{pad(m)}</option>{/each}
            </select>
          </div>
        </div>
      </div>
    {/if}
    <div class="dp-foot">
      <button type="button" class="dp-clear" onclick={clear}>Очистити</button>
      {#if mode === 'range'}
        <button
          type="button"
          class="dp-close"
          disabled={!from || !to}
          onclick={() => {
            onApply();
            open = false;
          }}
        >{applyLabel}</button>
      {:else}
        <button type="button" class="dp-close" onclick={() => (open = false)}>Готово</button>
      {/if}
    </div>
  </div>
{/if}

<style>
  .dp {
    display: flex;
    flex-direction: column;
    gap: 3px;
    position: relative;
  }
  .dp-label {
    font-size: 10px;
    font-weight: 600;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .dp-field {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-width: 120px;
    border: 1px solid var(--border2);
    border-radius: 8px;
    padding: 8px 10px;
    font-size: 12px;
    font-weight: 600;
    color: var(--text);
    background: var(--bg);
    font-family: inherit;
    cursor: pointer;
  }
  .dp-field.empty {
    color: var(--text-muted);
    font-weight: 500;
  }
  .dp-field svg {
    color: var(--accent-text);
    flex-shrink: 0;
  }

  .dp-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 1000;
    border: none;
    padding: 0;
    cursor: default;
  }
  .dp-pop {
    position: fixed;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: min(320px, calc(100vw - 32px));
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 16px;
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.25);
    z-index: 1001;
    padding: 14px;
    animation: dp-in 0.16s cubic-bezier(0.22, 1, 0.36, 1);
  }
  @keyframes dp-in {
    from { opacity: 0; transform: translate(-50%, -48%) scale(0.97); }
    to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  }
  .dp-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
  }
  .dp-title {
    font-size: 14px;
    font-weight: 700;
    color: var(--text);
  }
  .dp-nav {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1px solid var(--border2);
    background: var(--bg2);
    color: var(--text);
    font-size: 18px;
    line-height: 1;
    cursor: pointer;
    font-family: inherit;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .dp-nav:active { background: var(--pu-light); }
  .dp-wd {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    margin-bottom: 4px;
  }
  .dp-wd-cell {
    text-align: center;
    font-size: 10px;
    font-weight: 600;
    color: var(--text-muted);
    padding: 4px 0;
  }
  .dp-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 2px;
  }
  .dp-day,
  .dp-day-empty {
    aspect-ratio: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    border-radius: 8px;
  }
  .dp-day {
    border: none;
    background: transparent;
    color: var(--text);
    cursor: pointer;
    font-family: inherit;
    font-weight: 500;
  }
  .dp-day:active { background: var(--bg2); }
  .dp-day.today {
    border: 1px solid var(--pu2);
    font-weight: 700;
  }
  .dp-day.selected {
    background: var(--pu);
    color: var(--text-light);
    font-weight: 700;
  }
  .dp-day.in-range {
    background: var(--pu-light);
    border-radius: 0;
  }
  .dp-day.r-start,
  .dp-day.r-end {
    background: var(--pu);
    color: var(--text-light);
    font-weight: 700;
  }
  .dp-day.r-start { border-radius: 8px 0 0 8px; }
  .dp-day.r-end { border-radius: 0 8px 8px 0; }
  .dp-day:disabled {
    color: var(--text-muted);
    opacity: 0.35;
    cursor: not-allowed;
  }
  .dp-time {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid var(--border);
  }
  .dp-time-label {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-muted);
  }
  .dp-time-fields {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .dp-time-sel {
    border: 1px solid var(--border2);
    border-radius: 8px;
    background: var(--bg2);
    color: var(--text);
    font-family: inherit;
    font-size: 14px;
    font-weight: 700;
    padding: 7px 10px;
    cursor: pointer;
    outline: none;
    accent-color: var(--pu);
  }
  .dp-time-sel:focus { border-color: var(--pu); }
  .dp-time-colon {
    font-size: 14px;
    font-weight: 700;
    color: var(--text-muted);
  }
  .dp-foot {
    display: flex;
    justify-content: space-between;
    margin-top: 12px;
  }
  .dp-clear,
  .dp-close {
    border: none;
    background: transparent;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    padding: 6px 10px;
    border-radius: 8px;
  }
  .dp-clear { color: var(--text-muted); }
  .dp-close { color: var(--accent-text); }
  .dp-close:disabled { opacity: 0.4; cursor: not-allowed; }

  .dp-range-times {
    display: flex;
    gap: 10px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid var(--border);
  }
  .dp-rt { flex: 1; display: flex; flex-direction: column; gap: 5px; }
  .dp-rt > span { font-size: 11px; font-weight: 600; color: var(--text-muted); }
  .dp-rt-fields { display: flex; align-items: center; gap: 5px; }
  .dp-rt-fields .dp-time-sel { flex: 1; min-width: 0; padding: 7px 8px; }
</style>
